import { useQuery } from '@tanstack/react-query';
import { API_BASE } from '../utils';
import { apiFetch } from './fetch-utils';
import type {
  TeamSearchResponse,
  TeamResponse,
  TeamWCResponse,
  TeamSquadResponse,
  TeamSquadNationalitiesResponse,
  TeamCalendarResponse,
  TeamRecentMatchesResponse,
  TeamSeasonLeadersResponse,
  TeamSeasonCompetitionsResponse,
  TeamMatchStatsResponse,
  TeamStatPercentileResponse,
  TeamStatScatterResponse,
  DominantAreasResponse,
  BuildupShotsResponse,
  PassingLinesResponse,
  DefenceActionsResponse,
  TouchesResponse,
} from '../types/teams';
import type { Country } from '../types/utils';

// GET /teams/search
interface TeamSearchParams {
  q: string;
  limit?: number;
}

const fetchTeamSearch = ({ q, limit = 15 }: TeamSearchParams) => {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<TeamSearchResponse>(`${API_BASE}/teams/search?${params}`)
}

export const useTeamSearch = (params: TeamSearchParams) =>
  useQuery({
    queryKey: ['teamSearch', params.q, params.limit],
    queryFn: () => fetchTeamSearch(params),
    enabled: params.q.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

// GET /teams/countries
const fetchCountries = () =>
  apiFetch<Country[]>(`${API_BASE}/teams/countries`)

export const useFetchCountries = () =>
  useQuery({
    queryKey: ['countries'],
    queryFn: fetchCountries,
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })

// GET /teams/national-team/{country_id}
interface NationalTeamParams {
  country_id: number;
}

const fetchNationalTeam = ({ country_id }: NationalTeamParams) =>
  apiFetch<TeamResponse>(`${API_BASE}/teams/national-team/${country_id}`)

export const useFetchNationalTeam = (params: NationalTeamParams) =>
  useQuery({
    queryKey: ['nationalTeam', params.country_id],
    queryFn: () => fetchNationalTeam(params),
    enabled: params.country_id > 0,
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}
interface TeamParams {
  team_id: number;
}

const fetchTeam = ({ team_id }: TeamParams) =>
  apiFetch<TeamResponse>(`${API_BASE}/teams/${team_id}`)

export const useFetchTeam = (params: TeamParams) =>
  useQuery({
    queryKey: ['team', params.team_id],
    queryFn: () => fetchTeam(params),
    enabled: params.team_id > 0,
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/squad
interface TeamSquadParams {
  team_id: number;
  season_year?: number;
  stat?: string;
}

const fetchTeamSquad = ({ team_id, season_year = 2026, stat = 'appearances' }: TeamSquadParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  return apiFetch<TeamSquadResponse>(`${API_BASE}/teams/${team_id}/squad?${params}`)
}

export const useFetchTeamSquad = (params: TeamSquadParams) =>
  useQuery({
    queryKey: ['teamSquad', params.team_id, params.season_year, params.stat],
    queryFn: () => fetchTeamSquad(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/squad/nationalities
interface TeamSquadNationalitiesParams {
  team_id: number;
  season_year?: number;
}

const fetchTeamSquadNationalities = ({ team_id, season_year = 2026 }: TeamSquadNationalitiesParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  return apiFetch<TeamSquadNationalitiesResponse>(`${API_BASE}/teams/${team_id}/squad/nationalities?${params}`)
}

export const useFetchTeamSquadNationalities = (params: TeamSquadNationalitiesParams) =>
  useQuery({
    queryKey: ['teamSquadNationalities', params.team_id, params.season_year],
    queryFn: () => fetchTeamSquadNationalities(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/calendar
interface TeamCalendarParams {
  team_id: number;
  year: number;
  month: number;
}

const fetchTeamCalendar = ({ team_id, year, month }: TeamCalendarParams) => {
  const params = new URLSearchParams({ year: String(year), month: String(month) });
  return apiFetch<TeamCalendarResponse>(`${API_BASE}/teams/${team_id}/calendar?${params}`)
}

export const useFetchTeamCalendar = (params: TeamCalendarParams) =>
  useQuery({
    queryKey: ['teamCalendar', params.team_id, params.year, params.month],
    queryFn: () => fetchTeamCalendar(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/recent-matches
interface TeamRecentMatchesParams {
  team_id: number;
}

const fetchTeamRecentMatches = ({ team_id }: TeamRecentMatchesParams) =>
  apiFetch<TeamRecentMatchesResponse>(`${API_BASE}/teams/${team_id}/recent-matches`)

export const useFetchTeamRecentMatches = (params: TeamRecentMatchesParams) =>
  useQuery({
    queryKey: ['teamRecentMatches', params.team_id],
    queryFn: () => fetchTeamRecentMatches(params),
    enabled: params.team_id > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/season-leaders
interface TeamSeasonLeadersParams {
  team_id: number;
  season_year?: number;
  comp_ids?: number[] | null;
}

const fetchTeamSeasonLeaders = ({ team_id, season_year = 2026, comp_ids }: TeamSeasonLeadersParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<TeamSeasonLeadersResponse>(`${API_BASE}/teams/${team_id}/season-leaders?${params}`)
}

export const useFetchTeamSeasonLeaders = (params: TeamSeasonLeadersParams) =>
  useQuery({
    queryKey: ['teamSeasonLeaders', params.team_id, params.season_year, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchTeamSeasonLeaders(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/match-stats
interface TeamMatchStatsParams {
  team_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
}

const fetchTeamMatchStats = ({ team_id, season_year = 2026, stat = 'goals', comp_ids }: TeamMatchStatsParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<TeamMatchStatsResponse>(`${API_BASE}/teams/${team_id}/match-stats?${params}`)
}

export const useFetchTeamMatchStats = (params: TeamMatchStatsParams) =>
  useQuery({
    queryKey: ['teamMatchStats', params.team_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchTeamMatchStats(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/match-stats/percentile
interface TeamStatPercentileParams {
  team_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
}

const fetchTeamStatPercentile = ({ team_id, season_year = 2026, stat = 'goals', comp_ids }: TeamStatPercentileParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<TeamStatPercentileResponse>(`${API_BASE}/teams/${team_id}/match-stats/percentile?${params}`)
}

export const useFetchTeamStatPercentile = (params: TeamStatPercentileParams) =>
  useQuery({
    queryKey: ['teamStatPercentile', params.team_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchTeamStatPercentile(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/stat-scatter
interface TeamStatScatterParams {
  team_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
}

const fetchTeamStatScatter = ({ team_id, season_year = 2026, stat = 'goals', comp_ids }: TeamStatScatterParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<TeamStatScatterResponse>(`${API_BASE}/teams/${team_id}/stat-scatter?${params}`)
}

export const useFetchTeamStatScatter = (params: TeamStatScatterParams) =>
  useQuery({
    queryKey: ['teamStatScatter', params.team_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchTeamStatScatter(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/dominant-areas
interface DominantAreasParams {
  team_id: number;
  stat: string;
  season_year?: number;
  comp_ids?: number[] | null;
}

const fetchDominantAreas = ({ team_id, stat, season_year = 2026, comp_ids }: DominantAreasParams) => {
  const params = new URLSearchParams({ stat, season_year: String(season_year) });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<DominantAreasResponse>(`${API_BASE}/teams/${team_id}/dominant-areas?${params}`)
}

export const useFetchDominantAreas = (params: DominantAreasParams) =>
  useQuery({
    queryKey: ['dominantAreas', params.team_id, params.stat, params.season_year, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchDominantAreas(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/season-competitions
interface TeamSeasonCompetitionsParams {
  team_id: number;
  season_year?: number;
}

const fetchTeamSeasonCompetitions = ({ team_id, season_year = 2026 }: TeamSeasonCompetitionsParams) =>
  apiFetch<TeamSeasonCompetitionsResponse>(`${API_BASE}/teams/${team_id}/season-competitions?season_year=${season_year}`)

export const useFetchTeamSeasonCompetitions = (params: TeamSeasonCompetitionsParams) =>
  useQuery({
    queryKey: ['teamSeasonCompetitions', params.team_id, params.season_year],
    queryFn: () => fetchTeamSeasonCompetitions(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/wc2026
interface TeamWCParams {
  team_id: number
  stat?: string
  season_year?: number
}

const fetchTeamWC = ({ team_id, stat = "goals", season_year }: TeamWCParams) => {
  const params = new URLSearchParams({ stat })
  if (season_year !== undefined) params.set("season_year", String(season_year))
  return apiFetch<TeamWCResponse>(`${API_BASE}/teams/${team_id}/wc2026?${params}`)
}

export const useFetchTeamWC = (params: TeamWCParams) =>
  useQuery({
    queryKey: ["teamWC", params.team_id, params.stat, params.season_year],
    queryFn: () => fetchTeamWC(params),
    enabled: params.team_id > 0,
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })

// GET /nation-dist
interface NationDistParams {
  country_id?: number | null
  country2_id?: number | null
  stat?: string
  season_year: number
}

const fetchNationDist = ({ country_id, country2_id, stat = "goals", season_year }: NationDistParams) => {
  const params = new URLSearchParams({ stat, season_year: String(season_year) })
  if (country_id) params.set("country_id", String(country_id))
  if (country2_id) params.set("country2_id", String(country2_id))
  return apiFetch<TeamWCResponse>(`${API_BASE}/teams/nation-dist?${params}`)
}

export const useFetchNationDist = (params: NationDistParams) =>
  useQuery({
    queryKey: ["nationDist", params.country_id, params.country2_id, params.stat, params.season_year],
    queryFn: () => fetchNationDist(params),
    enabled: !!(params.country_id || params.country2_id),
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })





// GET /teams/{team_id}/buildup-shots
interface BuildupShotsParams {
  team_id: number;
  season_year?: number;
  comp_ids?: number[] | null;
  view?: 'for' | 'against';
}

const fetchBuildupShots = ({ team_id, season_year = 2026, comp_ids, view = 'for' }: BuildupShotsParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), view });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<BuildupShotsResponse>(`${API_BASE}/teams/${team_id}/buildup-shots?${params}`)
}

export const useFetchBuildupShots = (params: BuildupShotsParams) =>
  useQuery({
    queryKey: ['buildupShots', params.team_id, params.season_year, params.comp_ids?.join(',') ?? null, params.view ?? 'for'],
    queryFn: () => fetchBuildupShots(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/passing-lines
interface PassingLinesParams {
  team_id: number;
  season_year?: number;
  comp_ids?: number[] | null;
}

const fetchPassingLines = ({ team_id, season_year = 2026, comp_ids }: PassingLinesParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<PassingLinesResponse>(`${API_BASE}/teams/${team_id}/passing-lines?${params}`)
}

export const useFetchPassingLines = (params: PassingLinesParams) =>
  useQuery({
    queryKey: ['passingLines', params.team_id, params.season_year, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchPassingLines(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/defence-actions
interface DefenceActionsParams {
  team_id: number;
  season_year?: number;
  comp_ids?: number[] | null;
}

const fetchDefenceActions = ({ team_id, season_year = 2026, comp_ids }: DefenceActionsParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<DefenceActionsResponse>(`${API_BASE}/teams/${team_id}/defence-actions?${params}`)
}

export const useFetchDefenceActions = (params: DefenceActionsParams) =>
  useQuery({
    queryKey: ['defenceActions', params.team_id, params.season_year, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchDefenceActions(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /teams/{team_id}/touches
interface TeamTouchesParams {
  team_id: number;
  season_year?: number;
  comp_ids?: number[] | null;
}

const fetchTeamTouches = ({ team_id, season_year = 2026, comp_ids }: TeamTouchesParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<TouchesResponse>(`${API_BASE}/teams/${team_id}/touches?${params}`)
}

export const useFetchTeamTouches = (params: TeamTouchesParams) =>
  useQuery({
    queryKey: ['teamTouches', params.team_id, params.season_year, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchTeamTouches(params),
    enabled: params.team_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })
